"use client";

import { useEffectEvent, useLayoutEffect } from "react";

/**
 * اندازه‌گیری فقط از داخل یک ResizeObserver مشترک: یک بار بلافاصله بعد از اولین layout المان‌ها (قبل از paint، پس
 * پرشی دیده نمی‌شود) و دوباره هر بار که اندازه‌ی یکی از آن‌ها عوض شود.
 *
 * قبلاً هدرها، بنر، Collapse و آکاردئون اندازه را داخل `useLayoutEffect` / افکت (و روی `resize` پنجره) با
 * `getBoundingClientRect` / `offsetHeight` / `scrollHeight` می‌خواندند؛ این خواندن وسط commit ری‌اکت مرورگر را مجبور
 * به layout هم‌زمان می‌کرد (Lighthouse: forced reflow).
 *
 * همه‌ی مصرف‌کننده‌ها یک observer دارند و callback آن دو مرحله است: اول `measure` همه خوانده می‌شود، بعد نوشتن‌ها.
 * `measure` فقط بخواند و اگر باید DOM را تغییر دهد (مثلاً متغیر CSS)، آن را در تابعی برگرداند. با observerهای جدا،
 * هدر `--header-height` را می‌نوشت و Collapse که بعدش scrollHeight می‌خواند باز layout اجباری می‌ساخت.
 * setState نوشتن DOM نیست و می‌تواند داخل `measure` بماند.
 *
 * @param {() => (Element | null | undefined)[]} get_nodes المان‌هایی که باید زیر نظر باشند
 * @param {() => (void | (() => void))} measure خواندن اندازه‌ها؛ نوشتن DOM را به‌صورت تابع برمی‌گرداند
 * @param {any[]} deps وقتی المان‌ها عوض می‌شوند (مثلاً نسخه‌ی موبایل/دسکتاپ) دوباره observe می‌شوند
 */

// المان ← اندازه‌گیرهایی که آن را زیر نظر دارند
const node_handlers = new Map();
let shared_observer = null;

const run = (handlers) => {
	const writes = [];
	handlers.forEach((handler) => {
		const write = handler();
		if (typeof write === "function") writes.push(write);
	});
	writes.forEach((write) => write());
};

const get_observer = () => {
	if (shared_observer) return shared_observer;
	shared_observer = new ResizeObserver((entries) => {
		const due = new Set();
		entries.forEach((entry) => node_handlers.get(entry.target)?.forEach((handler) => due.add(handler)));
		run(due);
	});
	return shared_observer;
};

const watch = (node, handler) => {
	let handlers = node_handlers.get(node);
	if (!handlers) {
		handlers = new Set();
		node_handlers.set(node, handlers);
	}
	handlers.add(handler);
	// observe دوباره‌ی همان المان کاری نمی‌کند؛ پس برای اندازه‌گیری اول المانی که از قبل زیر نظر است، unobserve و
	// دوباره observe می‌شود تا callback اولیه برسد
	const observer = get_observer();
	observer.unobserve(node);
	observer.observe(node);
};

const unwatch = (node, handler) => {
	const handlers = node_handlers.get(node);
	if (!handlers) return;
	handlers.delete(handler);
	if (handlers.size === 0) {
		node_handlers.delete(node);
		shared_observer?.unobserve(node);
	}
};

export default function useResizeMeasure(get_nodes, measure, deps) {
	// آخرین نسخه‌ی measure (با state تازه) بدون اینکه با هر رندر دوباره observe شود
	const on_measure = useEffectEvent(measure);

	useLayoutEffect(() => {
		const nodes = get_nodes().filter(Boolean);
		if (!nodes.length) return;

		// مرورگر بدون ResizeObserver: همان رفتار قبلی، یک اندازه‌گیری مستقیم
		if (typeof ResizeObserver === "undefined") {
			run([on_measure]);
			return;
		}

		// تابع جدا برای هر اجرای افکت تا unwatch فقط سهم همین نمونه را بردارد
		const handler = () => on_measure();
		nodes.forEach((node) => watch(node, handler));
		return () => nodes.forEach((node) => unwatch(node, handler));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);
}
